import SelectOrgs from '@components/SelectOrgs'
import { useEffect, useRef, useState } from 'react'
import SelectRole, { RoleName } from '@components/SelectRole'
import { LoadingButton } from '@mui/lab'
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWork'
import {
    Alert,
    Box,
    CircularProgress,
    FormControlLabel,
    Stack,
    Switch,
    SxProps,
    Theme,
    Typography,
} from '@mui/material'
import { z } from 'zod'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import API from '@enums/api'
import { describeError } from '@utils/errors'
import { MergeBlockerSchema } from '@pages/Dashboard/actions/MergeAccountsPage/merge-schemas'

type PersonalToOrgProps = {
    userId: string
    sx?: SxProps<Theme>
}

const AddToOrgPlanSchema = z.object({
    blockers: z.array(MergeBlockerSchema),
})

type AddToOrgPlan = z.infer<typeof AddToOrgPlanSchema>

/**
 * Adds a personal account to an organisation, after the same checks a merge
 * runs. The preview loads as soon as an org and role are picked, so anything
 * in the way shows up before Submit rather than as an error after it.
 */
export default function PersonalUserToOrgUserForm(props: PersonalToOrgProps) {
    const { userId, sx } = props
    const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
        undefined
    )
    const [selectedRole, setSelectedRole] = useState<RoleName | undefined>(
        'user'
    )
    const [loading, setLoading] = useState(false)
    const [dryRun, setDryRun] = useState(false)
    const [plan, setPlan] = useState<AddToOrgPlan | null>(null)
    const [planLoading, setPlanLoading] = useState(false)
    const [planError, setPlanError] = useState<string | null>(null)
    const latestPreview = useRef(0)

    useEffect(() => {
        setPlan(null)
        setPlanError(null)

        if (!selectedOrg || !selectedRole) {
            return
        }

        const requestId = latestPreview.current + 1
        latestPreview.current = requestId

        const loadPreview = async () => {
            setPlanLoading(true)

            const res = await API.actions.previewAddPersonalUserToOrg({
                id: userId,
                role: selectedRole,
                org_id: selectedOrg,
            })

            if (requestId !== latestPreview.current) {
                return
            }

            setPlanLoading(false)

            if (!res.ok) {
                setPlanError(describeError(res, 'Could not run the checks.'))
                return
            }

            const { data, error } = AddToOrgPlanSchema.safeParse(res.value)

            if (error) {
                console.error(error)
                setPlanError('Unexpected response from the checks.')
                return
            }

            setPlan(data)
        }

        loadPreview()
    }, [userId, selectedOrg, selectedRole])

    const blocked = (plan?.blockers.length ?? 0) > 0
    const canSubmit = Boolean(plan) && !blocked && !planLoading

    const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault()

        if (!selectedRole || !selectedOrg || !canSubmit) return

        setLoading(true)
        const res = await API.actions.addPersonalUserToOrg({
            id: userId,
            role: selectedRole,
            org_id: selectedOrg,
            dryRun,
        })

        setLoading(false)
        if (!res.ok) {
            enqueueSnackbar(describeError(res, 'Could not add user to org'), {
                variant: 'error',
            })
            return
        }

        enqueueSnackbar(
            dryRun
                ? 'Test passed. Nothing was saved.'
                : 'User added to org. Reload the page to see it.',
            { variant: 'success' }
        )
    }

    return (
        <Box
            sx={{
                ...sx,
                marginTop: 1,
                marginBottom: 1,
            }}
        >
            <form onSubmit={onSubmit} id="personal-to-org-form">
                <Typography variant="h5">Add User to Org</Typography>
                <SelectOrgs
                    selectedOrg={selectedOrg}
                    setSelectedOrg={setSelectedOrg}
                    sx={{
                        marginBottom: 2,
                    }}
                    required={true}
                />
                <SelectRole
                    setSelectedRole={setSelectedRole}
                    defaultValue="user"
                    options={['user', 'atsp-user']}
                    sx={{
                        marginBottom: 2,
                    }}
                    required={true}
                />

                <Stack direction="column" gap="0.75em" aria-live="polite">
                    {planLoading && (
                        <Stack direction="row" gap="0.5em" role="status">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Checking this account...
                            </Typography>
                        </Stack>
                    )}

                    {planError && <Alert severity="warning">{planError}</Alert>}

                    {plan && !planLoading && blocked && (
                        <Typography variant="subtitle2" color="error">
                            This cannot run yet
                        </Typography>
                    )}

                    {plan &&
                        !planLoading &&
                        plan.blockers.map((blocker) => (
                            <Alert key={blocker.code} severity="error">
                                {blocker.message}
                                {blocker.overridable && (
                                    <Typography variant="caption" component="p">
                                        A developer can override this one once
                                        it has been agreed (
                                        <code>{blocker.code}</code>).
                                    </Typography>
                                )}
                            </Alert>
                        ))}
                </Stack>

                <FormControlLabel
                    control={
                        <Switch
                            checked={dryRun}
                            disabled={loading}
                            onChange={(event) =>
                                setDryRun(event.target.checked)
                            }
                        />
                    }
                    label="Test only, do not save anything"
                />
                <LoadingButton
                    sx={{ marginTop: '16px' }}
                    startIcon={<AddHomeWorkIcon />}
                    size="small"
                    variant="contained"
                    color={dryRun ? 'inherit' : 'primary'}
                    type="submit"
                    loading={loading}
                    disabled={!canSubmit}
                >
                    {dryRun ? 'Test' : 'Submit'}
                </LoadingButton>
            </form>
            <SnackbarProvider
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            />
        </Box>
    )
}
